import React from "react";
import {
  Box,
  Card,
  Typography,
  Skeleton,
  TableCell,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Divider,
  CircularProgress,
  TextField,
  Alert,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import SearchOffIcon from "@material-ui/icons/SearchOff";
import DataTable from "src/components/DataTable";
import { filterModes } from "../../utils/filter-utils";
import { formatInteger } from "../../utils/format-utils";

export default function DataSummaryComponent({
  year,
  setYear,
  month,
  setMonth,
  yearOptions,
  monthOptions,
  data,
  isDataSummaryLoading,
  handleExport,
  itemPerPage,
  enableItemPerPageOption,
  handleItemPerPageChange,
  onDataSummaryPageClick,
  dataSummaryPage,
  totalQrCreditCount,
  isExporting,
  filterMode,
  onModeChange,
  selectedDateRange,
  onDateRangeChange,
}) {
  const [dateError, setDateError] = React.useState(null);

  const handleStartDateChange = (newValue) => {
    if (!newValue) {
      setDateError("Start date is required");
      return;
    }

    setDateError(null);
    const endDate = selectedDateRange[1];

    if (endDate && endDate < newValue) {
      setDateError("End date cannot be before start date");
      return;
    }

    onDateRangeChange([newValue, endDate]);
  };

  const handleEndDateChange = (newValue) => {
    if (!newValue) {
      setDateError("End date is required");
      return;
    }

    setDateError(null);
    const startDate = selectedDateRange[0];

    if (!startDate) {
      onDateRangeChange([newValue, newValue]);
      return;
    }

    if (newValue < startDate) {
      setDateError("End date cannot be before start date");
      return;
    }

    onDateRangeChange([startDate, newValue]);
  };

  const renderFilterControls = () => {
    const formControlStyle = {
      minWidth: 120,
    };

    const selectStyle = {
      height: "32px",
    };

    switch (filterMode) {
      case filterModes.YEAR:
        return (
          <FormControl sx={formControlStyle}>
            <InputLabel shrink>Year</InputLabel>
            <Select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              label="Year"
              notched
              sx={selectStyle}
              size="small"
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case filterModes.MONTH:
        return (
          <Box display="flex" gap={2}>
            <FormControl sx={formControlStyle}>
              <InputLabel shrink>Year</InputLabel>
              <Select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                label="Year"
                notched
                sx={selectStyle}
                size="small"
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={formControlStyle}>
              <InputLabel shrink>Month</InputLabel>
              <Select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                label="Month"
                notched
                sx={selectStyle}
                size="small"
              >
                {monthOptions.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case filterModes.DAY:
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" gap={2} alignItems="center">
                <Box sx={{ position: "relative", width: 200 }}>
                  <DesktopDatePicker
                    label="Start Date"
                    value={selectedDateRange[0]}
                    onChange={handleStartDateChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        sx={{
                          width: "100%",
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                        }}
                        error={!!dateError && dateError.includes("Start")}
                      />
                    )}
                    inputFormat="dd MMM yyyy"
                    disableMaskedInput
                  />
                </Box>

                <Typography variant="body2" color="textSecondary">
                  to
                </Typography>

                <Box sx={{ position: "relative", width: 200 }}>
                  <DesktopDatePicker
                    label="End Date"
                    value={selectedDateRange[1]}
                    onChange={handleEndDateChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        sx={{
                          width: "100%",
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                        }}
                        error={!!dateError && dateError.includes("End")}
                      />
                    )}
                    inputFormat="dd MMM yyyy"
                    disableMaskedInput
                  />
                </Box>
              </Box>

              {dateError && (
                <Alert severity="error" sx={{ py: 0 }}>
                  {dateError}
                </Alert>
              )}
            </Box>
          </LocalizationProvider>
        );

      default:
        return null;
    }
  };

  return (
    <Card sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" fontWeight="bold" mb={1}>
        QR Credit Summary Data
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Box display="flex" gap={4} mb={3} flexWrap="wrap" alignItems="center">
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Filter Mode</InputLabel>
          <Select
            value={filterMode}
            onChange={(e) => {
              setDateError(null);
              onModeChange(e.target.value);
            }}
            label="Filter Mode"
            size="small"
            sx={{ height: "32px" }}
          >
            <MenuItem value={filterModes.YEAR}>By Year</MenuItem>
            <MenuItem value={filterModes.MONTH}>By Month</MenuItem>
            <MenuItem value={filterModes.DAY}>By Date Range</MenuItem>
          </Select>
        </FormControl>
        {renderFilterControls()}
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        mb={3}
        flexWrap="wrap"
      >
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="subtitle1" fontWeight="bold">
            Export as:
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ minWidth: 100 }}
            onClick={() => handleExport("csv")}
            disabled={isExporting || !!dateError}
            startIcon={isExporting ? <CircularProgress size={16} /> : null}
          >
            CSV
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ minWidth: 100 }}
            onClick={() => handleExport("excel")}
            disabled={isExporting || !!dateError}
            startIcon={isExporting ? <CircularProgress size={16} /> : null}
          >
            EXCEL
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ minWidth: 100 }}
            onClick={() => handleExport("txt")}
            disabled={isExporting || !!dateError}
            startIcon={isExporting ? <CircularProgress size={16} /> : null}
          >
            TXT
          </Button>
        </Box>
      </Box>

      {isDataSummaryLoading ? (
        <>
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
        </>
      ) : !data.length ? (
        <Box display="flex" flexDirection="column" alignItems="center" my={4}>
          <SearchOffIcon color="disabled" style={{ fontSize: 40 }} />
          <Typography variant="body1" color="textSecondary" mt={2}>
            No data found for this period.
          </Typography>
        </Box>
      ) : (
        <DataTable
          isLoading={isDataSummaryLoading}
          rows={data}
          rowsPerPage={itemPerPage}
          total={totalQrCreditCount}
          page={dataSummaryPage}
          renderHeader={() => (
            <>
              <TableCell>
                <Typography fontWeight="bold">Company Name</Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight="bold">Credited Amount</Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight="bold">Deducted Amount</Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight="bold">Consumed Amount</Typography>
              </TableCell>
            </>
          )}
          renderRow={(row) => (
            <>
              <TableCell>{row.company}</TableCell>
              <TableCell>{formatInteger(row.credited_amount)}</TableCell>
              <TableCell>{formatInteger(row.deducted_amount)}</TableCell>
              <TableCell>{formatInteger(row.generated_amount)}</TableCell>
            </>
          )}
          onPageClick={onDataSummaryPageClick}
          enableItemPerPageOption={enableItemPerPageOption}
          handleItemPerPageChange={handleItemPerPageChange}
        />
      )}
    </Card>
  );
}
